import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ApiService } from '../utils/api';
import { CheckCircle, Star } from 'lucide-react';

const TestimonialSubmitPage = () => {
  const [form, setForm] = useState({ name: '', title: '', message: '', rating: 5, website: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError('');
    try {
      await ApiService.submitTestimonial(form);
      setStatus('done');
    } catch (err) {
      setError(err.message);
      setStatus('idle');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <Link to="/"><img src="/logo.png" alt="Home" className="h-12 w-auto mb-2" /></Link>
          <CardTitle className="text-xl">Share your experience</CardTitle>
          <p className="text-sm text-gray-600">We'll show it on our website once it's reviewed.</p>
        </CardHeader>
        <CardContent>
          {status === 'done' ? (
            <div className="text-center space-y-3 py-6">
              <CheckCircle className="h-10 w-10 text-green-600 mx-auto" />
              <p className="font-medium">Thank you! Your testimonial has been sent for review.</p>
              <Link to="/" className="text-blue-600 hover:underline text-sm">Back to home</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="t-name">Your name *</Label>
                <Input id="t-name" required maxLength={80} value={form.name} onChange={set('name')} />
              </div>
              <div>
                <Label htmlFor="t-title">Role / organisation</Label>
                <Input id="t-title" maxLength={120} placeholder="e.g. Organiser, Tech Summit 2026" value={form.title} onChange={set('title')} />
              </div>
              <div>
                <Label>Rating</Label>
                <div className="flex gap-1 mt-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" aria-label={`${n} star${n > 1 ? 's' : ''}`}
                      onClick={() => setForm((prev) => ({ ...prev, rating: n }))}>
                      <Star className={`h-7 w-7 ${n <= form.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="t-message">Your experience *</Label>
                <Textarea id="t-message" required minLength={10} maxLength={1000} rows={5} value={form.message} onChange={set('message')} />
                <div className="text-xs text-gray-400 text-right">{form.message.length}/1000</div>
              </div>
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                className="hidden" value={form.website} onChange={set('website')} />
              {error && <p className="text-sm text-red-600">{error}</p>}
              <Button type="submit" className="w-full" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Submit'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TestimonialSubmitPage;
